import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BCMReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.showSuccess) {
      toast.success("🎉 Your BCM assessment report is now available.", { autoClose: 5000 });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const getLevelLabel = (level) => {
    if (!level) return "Unknown";
    if (typeof level === "object") return `${level.level} - ${level.label}`;
    switch(level) {
      case 0: return "0 - Incomplete / Not Performed";
      case 1: return "1 - Initial";
      case 2: return "2 - Managed";
      case 3: return "3 - Defined";
      default: return "Unknown";
    }
  };

  // Fetch BCM report
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    axios.get(`${process.env.REACT_APP_API_URL}maturity/bcm/report/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      setReportData(res.data.phases || []);
      setLoading(false);
    })
    .catch(err => {
      console.error(err);
      setError("Failed to load BCM report");
      setLoading(false);
    });
  }, []);

  const handleExportPDF = () => {
    if (!reportRef.current) return;
    setExporting(true);

    html2canvas(reportRef.current, { scale: 2 }).then(canvas => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const startY = 20;
      const usablePageHeight = pageHeight - startY - margin;

      pdf.setFontSize(18);
      pdf.setTextColor(181,136,0);
      pdf.text("BCM Assessment Report", pageWidth/2, 15, { align: "center" });

      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= usablePageHeight) {
        pdf.addImage(imgData, "PNG", margin, startY, imgWidth, imgHeight);
      } else {
        let yOffset = 0;
        const totalHeight = canvas.height;
        const pageHeightPx = (usablePageHeight * canvas.width) / imgWidth;

        while(yOffset < totalHeight){
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.min(canvas.height - yOffset, pageHeightPx);
          const ctx = sliceCanvas.getContext("2d");
          ctx.drawImage(canvas, 0, yOffset, canvas.width, sliceCanvas.height, 0, 0, sliceCanvas.width, sliceCanvas.height);
          const pageImage = sliceCanvas.toDataURL("image/png");
          const displayHeight = (sliceCanvas.height * imgWidth)/canvas.width;
          if(yOffset>0) pdf.addPage();
          pdf.addImage(pageImage,"PNG", margin,startY,imgWidth,displayHeight);
          yOffset += sliceCanvas.height;
        }
      }

      pdf.save(`bcm-report-${new Date().toISOString().split("T")[0]}.pdf`);
      setExporting(false);
    }).catch(err => { console.error(err); setExporting(false); });
  };

  if(loading) return <div className="text-center text-amber-600">Loading report...</div>;
  if(error) return <div className="text-center text-red-600">{error}</div>;

const chartData = reportData.map(phase => {
  const total = phase.total_controls || 0;
  const compliant = Math.min(phase.compliant_controls || 0, total);
  const percent = total === 0 ? 0 : (compliant/total)*100;

  return { 
    phase: phase.code ? `[${phase.code}] ${phase.phase}` : phase.phase || "N/A",
    compliance: parseFloat(percent.toFixed(2)) 
  };
});


  return (
    <div className="max-w-6xl mx-auto p-6">
      <div ref={reportRef}>
        <h1 className="text-3xl font-bold mb-6 text-amber-600">BCM Assessment Report</h1>

        {reportData.map((phase, idx) => (
          <div key={idx} className="mb-8 p-4 bg-amber-50 rounded shadow">

            {/* Phase Header */}
            <h2 className="text-xl font-semibold mb-2 text-amber-600">
              {phase.code ? `[${phase.code}] ` : ''}{phase.phase}
            </h2>

            {/* Check if assessment is draft */}
            {phase.status === "draft" ? (
              <p className="text-gray-500 italic">
                Assessment not submitted yet. Results will appear after submission.
              </p>
            ) : (
              <>
                {/* Phase Summary Table */}
                <table className="w-full border-collapse border border-gray-300 mb-4">
                  <thead>
                    <tr>
                      <th className="border px-2 py-1 bg-amber-100">Total Controls</th>
                      <th className="border px-2 py-1 bg-amber-100">Compliant Controls</th>
                      <th className="border px-2 py-1 bg-amber-100">Compliance %</th>
                      <th className="border px-2 py-1 bg-amber-100">Current Level</th>
                      <th className="border px-2 py-1 bg-amber-100">Target Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border px-2 py-1">{phase.total_controls}</td>
                      <td className="border px-2 py-1">{phase.compliant_controls}</td>
                      <td className="border px-2 py-1">{phase.compliance_percent?.toFixed(2)}%</td>
                      <td className="border px-2 py-1">{getLevelLabel(phase.current_level)}</td>
                      <td className="border px-2 py-1">{getLevelLabel(phase.target_level)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Focus Areas with Gaps */}
                {phase.focus_areas
                  .filter(fa => fa.questions?.some(q => q.gaps?.length > 0))
                  .map((fa, fidx) => (
                    <div key={fidx} className="mt-4 p-2 border rounded bg-white">
                      <h3 className="font-semibold mb-2">
                        {fa.code ? `[${fa.code}] ` : ''}{fa.title || fa.focus_area}
                      </h3>
                      {fa.questions
                        .filter(q => q.gaps?.length > 0)
                        .map(q => (
                          <div key={q.id} className="mt-2 p-2 border rounded bg-gray-50">
                            <p className="font-medium">
                              Question: {q.code ? `[${q.code}] ` : ''}{q.request}
                            </p>
                            {q.gaps.map((gap, gidx) => (
                              <div key={gidx} className="ml-4 mt-1">
                                <p><strong>Gap:</strong> {gap.gap}</p>
                                <p><strong>Recommendation:</strong> {gap.recommendation}</p>
                                <p><strong>Weight:</strong> {gap.weight}</p>
                              </div>
                            ))}
                          </div>
                        ))}
                    </div>
                ))}
              </>
            )}

          </div>
        ))}


        {/* Compliance Chart */}
        {chartData.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-2 text-amber-600">Phase Compliance Chart</h2>
            <div className="bg-amber-50 p-4 rounded shadow">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="phase"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={60}
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="compliance" fill="#D97706">
                    <LabelList
                      dataKey="compliance"
                      position="insideTop"
                      formatter={(value) => `${value}%`}
                      style={{ fill: 'white', fontSize: 12, fontWeight: 600 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-4 mt-8">
        <button onClick={() => navigate("/bcm/dashboard")} className="bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700">View Dashboard</button>
        <button onClick={handleExportPDF} disabled={exporting} className={`bg-amber-600 text-white px-4 py-2 rounded ${exporting ? "opacity-50 cursor-not-allowed" : "hover:bg-amber-700"}`}>{exporting ? "Generating..." : "Export PDF"}</button>
      </div>
    </div>
  );
};

export default BCMReport;
